/**
 * Recommendations Products Controller (Grid Layout)
 * Fetches and renders personalized product recommendations in grid format
 */

(function() {
  'use strict';

  const SUPABASE_URL = 'https://bernard-recommender.bernard-legoff-29.workers.dev';
  const PRODUCT_LIMIT = 12;

  const FACET_LABELS = {
    brand_dna: (label) => `la marque ${label}`,
    competition_type: (label) => `la ${label}`,
    scale: (label) => `le ${label}`,
    origin: (label) => `les voitures ${label}`,
    era_period: (label) => `les années ${label}`,
    theme_use: (label) => `les ${label}`
  };

  const elements = {
    loading: null,
    empty: null,
    content: null,
    sectionLiked: null,
    sectionRecommendations: null,
    sectionFocused: null,
    sectionSimilar: null,
    sectionAlsoViewed: null,
    focusedTitle: null,
    similarTitle: null
  };

  function init() {
    elements.loading = document.getElementById('reco-products-loading');
    elements.empty = document.getElementById('reco-products-empty');
    elements.content = document.getElementById('reco-products-content');
    elements.sectionLiked = document.getElementById('reco-section-liked');
    elements.sectionRecommendations = document.getElementById('reco-section-recommendations');
    elements.sectionFocused = document.getElementById('reco-section-focused');
    elements.sectionSimilar = document.getElementById('reco-section-similar');
    elements.sectionAlsoViewed = document.getElementById('reco-section-also-viewed');
    elements.focusedTitle = document.getElementById('reco-focused-title');
    elements.similarTitle = document.getElementById('reco-similar-title');

    if (!elements.content) {
      console.error('[RecoProducts] Page elements not found');
      return;
    }

    loadRecommendations();
  }

  async function getVisitorId() {
    if (window._rm && typeof window._rm.gv === 'function') {
      try {
        const visitorId = await window._rm.gv();
        if (visitorId) return visitorId;
      } catch (e) {
        console.warn('[RecoProducts] Failed to get visitor ID from _rm:', e);
      }
    }

    await new Promise(resolve => setTimeout(resolve, 1000));

    if (window._rm && typeof window._rm.gv === 'function') {
      try {
        return await window._rm.gv();
      } catch (e) {
        console.warn('[RecoProducts] Retry failed:', e);
      }
    }

    return null;
  }

  async function fetchRecommendations(visitorId) {
    const response = await fetch(`${SUPABASE_URL}/functions/v1/recommendations`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ visitorId, limit: PRODUCT_LIMIT })
    });

    if (!response.ok) throw new Error(`Recommendations API error: ${response.status}`);
    return response.json();
  }

  async function fetchFocusedRecommendations(visitorId) {
    const response = await fetch(`${SUPABASE_URL}/functions/v1/focused-recommendations`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ visitorId, limit: PRODUCT_LIMIT })
    });

    if (!response.ok) throw new Error(`Focused recommendations API error: ${response.status}`);
    return response.json();
  }

  async function fetchAlsoViewed(visitorId) {
    const response = await fetch(`${SUPABASE_URL}/functions/v1/also-viewed`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ visitorId, limit: PRODUCT_LIMIT })
    });

    if (!response.ok) throw new Error(`Also viewed API error: ${response.status}`);
    return response.json();
  }

  async function fetchSimilarProductsAuto(visitorId) {
    const response = await fetch(`${SUPABASE_URL}/functions/v1/similar-products-auto`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ visitorId, limit: PRODUCT_LIMIT })
    });

    if (!response.ok) throw new Error(`Similar products auto API error: ${response.status}`);
    return response.json();
  }

  async function fetchLikedProducts(visitorId) {
    const response = await fetch(`${SUPABASE_URL}/functions/v1/liked-products`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ visitorId, limit: PRODUCT_LIMIT })
    });

    if (!response.ok) throw new Error(`Liked products API error: ${response.status}`);
    return response.json();
  }

  function getFocusedTitle(meta) {
    if (!meta || !meta.selectedFacet || !meta.facetLabel) {
      return 'Parce que vous avez regardé';
    }

    const formatter = FACET_LABELS[meta.selectedFacet];
    if (formatter) {
      return `Parce que vous avez regardé ${formatter(meta.facetLabel)}`;
    }

    return `Parce que vous avez regardé ${meta.facetLabel}`;
  }

  const INITIAL_VISIBLE_DESKTOP = 3;
  const INITIAL_VISIBLE_MOBILE = 2;

  function getInitialVisible() {
    return window.innerWidth < 768 ? INITIAL_VISIBLE_MOBILE : INITIAL_VISIBLE_DESKTOP;
  }

  function filterInStock(products) {
    return (products || []).filter(p => {
      const qty = p.totalInventory;
      if (qty === null || qty === undefined) return true;
      return qty > 0;
    });
  }

  function renderSection(sectionElement, products, context, feedbackMap = {}, options = {}) {
    if (!sectionElement || !products || products.length === 0) {
      if (sectionElement) {
        sectionElement.style.display = 'none';
      }
      return false;
    }

    const { showFeedback = true } = options;
    const layout = sectionElement.dataset.layout || 'grid';
    const isGrid = layout === 'grid';

    // Find the appropriate container based on layout
    const container = isGrid
      ? sectionElement.querySelector('.products.grid')
      : sectionElement.querySelector('.products.collection');

    if (!container) return false;

    const productsWithFeedback = products.map(p => ({
      ...p,
      feedback: feedbackMap[p.id] || null
    }));

    if (window.ProductCardDynamic) {
      window.ProductCardDynamic.renderGrid(container, productsWithFeedback, {
        impressionContext: context,
        enableHoverImages: isGrid,
        hoverImagesCount: 2,
        showFeedback
      });
    }

    setupCollapsible(sectionElement, products.length, isGrid);

    sectionElement.style.display = 'block';
    return true;
  }

  function setupCollapsible(sectionElement, totalProducts, isGrid = true) {
    const container = isGrid
      ? sectionElement.querySelector('.products.grid')
      : sectionElement.querySelector('.products.collection');

    if (!container) return;

    const initialVisible = getInitialVisible();
    const cards = container.querySelectorAll('product-card');
    if (cards.length <= initialVisible) return;

    cards.forEach((card, index) => {
      if (index >= initialVisible) {
        card.classList.add('collapsed-item');
        card.style.display = 'none';
      }
    });

    const existingWrapper = sectionElement.querySelector('.reco-show-more-wrapper');
    if (existingWrapper) existingWrapper.remove();

    const wrapper = document.createElement('div');
    wrapper.className = 'reco-show-more-wrapper';

    const showMoreBtn = document.createElement('button');
    showMoreBtn.type = 'button';
    showMoreBtn.className = 'button outline reco-show-more-btn';
    showMoreBtn.innerHTML = `Voir plus (${totalProducts - initialVisible})`;
    showMoreBtn.dataset.expanded = 'false';

    showMoreBtn.addEventListener('click', () => {
      const isExpanded = showMoreBtn.dataset.expanded === 'true';

      cards.forEach((card, index) => {
        if (index >= initialVisible) {
          card.style.display = isExpanded ? 'none' : '';
        }
      });

      showMoreBtn.dataset.expanded = isExpanded ? 'false' : 'true';
      showMoreBtn.innerHTML = isExpanded
        ? `Voir plus (${totalProducts - initialVisible})`
        : 'Voir moins';
    });

    wrapper.appendChild(showMoreBtn);
    container.after(wrapper);
  }

  function showLoading() {
    if (elements.loading) elements.loading.style.display = 'flex';
    if (elements.empty) elements.empty.style.display = 'none';
    if (elements.content) elements.content.style.display = 'none';
  }

  function showEmpty() {
    if (elements.loading) elements.loading.style.display = 'none';
    if (elements.empty) elements.empty.style.display = 'block';
    if (elements.content) elements.content.style.display = 'none';
  }

  function showContent() {
    if (elements.loading) elements.loading.style.display = 'none';
    if (elements.empty) elements.empty.style.display = 'none';
    if (elements.content) elements.content.style.display = 'block';
  }

  async function fetchFeedbackState(visitorId) {
    try {
      const response = await fetch(`${SUPABASE_URL}/functions/v1/get-feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ visitorId })
      });

      if (!response.ok) return {};
      const data = await response.json();
      return data?.feedback || {};
    } catch (e) {
      console.warn('[RecoProducts] Failed to fetch feedback state:', e);
      return {};
    }
  }

  async function loadRecommendations() {
    showLoading();

    try {
      const visitorId = await getVisitorId();

      if (!visitorId) {
        console.warn('[RecoProducts] No visitor ID available');
        showEmpty();
        return;
      }

      console.log('[RecoProducts] Fetching recommendations for visitor:', visitorId);

      const [
        likedResult,
        recommendationsResult,
        focusedResult,
        similarResult,
        alsoViewedResult,
        feedbackResult
      ] = await Promise.allSettled([
        fetchLikedProducts(visitorId),
        fetchRecommendations(visitorId),
        fetchFocusedRecommendations(visitorId),
        fetchSimilarProductsAuto(visitorId),
        fetchAlsoViewed(visitorId),
        fetchFeedbackState(visitorId)
      ]);

      const liked = likedResult.status === 'fulfilled' ? likedResult.value : null;
      const recommendations = recommendationsResult.status === 'fulfilled' ? recommendationsResult.value : null;
      const focused = focusedResult.status === 'fulfilled' ? focusedResult.value : null;
      const similar = similarResult.status === 'fulfilled' ? similarResult.value : null;
      const alsoViewed = alsoViewedResult.status === 'fulfilled' ? alsoViewedResult.value : null;
      const feedbackMap = feedbackResult.status === 'fulfilled' ? feedbackResult.value : {};

      if (likedResult.status === 'rejected') {
        console.error('[RecoProducts] Liked fetch failed:', likedResult.reason);
      }
      if (recommendationsResult.status === 'rejected') {
        console.error('[RecoProducts] Recommendations fetch failed:', recommendationsResult.reason);
      }
      if (focusedResult.status === 'rejected') {
        console.error('[RecoProducts] Focused fetch failed:', focusedResult.reason);
      }
      if (similarResult.status === 'rejected') {
        console.error('[RecoProducts] Similar fetch failed:', similarResult.reason);
      }
      if (alsoViewedResult.status === 'rejected') {
        console.error('[RecoProducts] Also viewed fetch failed:', alsoViewedResult.reason);
      }

      if (liked?.products) liked.products = filterInStock(liked.products);
      if (recommendations?.products) recommendations.products = filterInStock(recommendations.products);
      if (focused?.products) focused.products = filterInStock(focused.products);
      if (similar?.products) similar.products = filterInStock(similar.products);
      if (alsoViewed?.products) alsoViewed.products = filterInStock(alsoViewed.products);

      const hasLiked = liked?.products?.length > 0;
      const hasRecommendations = recommendations?.products?.length > 0;
      const hasFocused = focused?.products?.length > 0;
      const hasSimilar = similar?.products?.length > 0;
      const hasAlsoViewed = alsoViewed?.products?.length > 0;

      if (!hasLiked && !hasRecommendations && !hasFocused && !hasSimilar && !hasAlsoViewed) {
        console.log('[RecoProducts] No recommendations available');
        showEmpty();
        return;
      }

      showContent();

      // Section 1: Liked Products (List) - only if visitor has liked products
      if (hasLiked) {
        renderSection(elements.sectionLiked, liked.products, 'liked-list', feedbackMap);
        console.log('[RecoProducts] Rendered liked:', liked.products.length, 'products');
      }

      // Section 2: Recommendations (Grid)
      if (hasRecommendations) {
        renderSection(elements.sectionRecommendations, recommendations.products, 'recommendations-grid', feedbackMap);
        console.log('[RecoProducts] Rendered recommendations:', recommendations.products.length, 'products', recommendations.meta?.algorithm);
      }

      // Section 3: Similar Products (List)
      if (hasSimilar) {
        if (elements.similarTitle && similar.meta?.displayMessage) {
          elements.similarTitle.textContent = similar.meta.displayMessage;
        }
        renderSection(elements.sectionSimilar, similar.products, 'similar-list', feedbackMap);
        console.log('[RecoProducts] Rendered similar:', similar.products.length, 'products', similar.meta?.anchorProduct?.title);
      }

      // Section 4: Focused Recommendations (Grid)
      if (hasFocused) {
        if (elements.focusedTitle && focused.meta?.displayMessage) {
          elements.focusedTitle.textContent = focused.meta.displayMessage;
        } else if (elements.focusedTitle && focused.meta) {
          elements.focusedTitle.textContent = getFocusedTitle(focused.meta);
        }
        renderSection(elements.sectionFocused, focused.products, 'focused-grid', feedbackMap);
        console.log('[RecoProducts] Rendered focused:', focused.products.length, 'products', focused.meta?.selectedFacet, focused.meta?.facetLabel);
      }

      // Section 5: Also Viewed (List) - no feedback buttons
      if (hasAlsoViewed) {
        renderSection(elements.sectionAlsoViewed, alsoViewed.products, 'also-viewed-list', feedbackMap, { showFeedback: false });
        console.log('[RecoProducts] Rendered also viewed:', alsoViewed.products.length, 'products');
      }

      setupImpressionTracking();

    } catch (error) {
      console.error('[RecoProducts] Error loading recommendations:', error);
      showEmpty();
    }
  }

  function setupImpressionTracking() {
    const productCards = document.querySelectorAll('#reco-products-content [data-product-gid]');

    if (productCards.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;

          const el = entry.target;
          const gid = el.dataset.productGid;
          const context = el.dataset.impressionContext || 'recommendations-grid';

          if (gid && window._rm && typeof window._rm.seen === 'function') {
            const seen = window._rm.seen();
            if (!seen[context]) {
              seen[context] = new Set();
            }
            seen[context].add(gid);
          }

          observer.unobserve(el);
        });
      },
      { threshold: 0.5 }
    );

    productCards.forEach(card => observer.observe(card));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
